import { useRef, useEffect, useCallback } from 'react';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

const globalCache = new Map<string, GLTF>();
const loader = new GLTFLoader();

export function useHardwareModels() {
  const loadedIds = useRef(new Set<string>());

  const loadModel = useCallback(async (hardwareId: string, url: string): Promise<GLTF | null> => {
    if (globalCache.has(hardwareId)) return globalCache.get(hardwareId)!;
    try {
      const gltf = await loader.loadAsync(url);
      globalCache.set(hardwareId, gltf);
      loadedIds.current.add(hardwareId);
      return gltf;
    } catch (err) {
      console.error(`Failed to load hardware model ${hardwareId}:`, err);
      return null;
    }
  }, []);

  const getModel = useCallback((hardwareId: string): GLTF | null => {
    return globalCache.get(hardwareId) ?? null;
  }, []);

  useEffect(() => {
    return () => {
      for (const id of loadedIds.current) {
        const gltf = globalCache.get(id);
        if (gltf) {
          gltf.scene.traverse((child: any) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) child.material.forEach((m: any) => m.dispose());
              else child.material.dispose();
            }
          });
          globalCache.delete(id);
        }
      }
    };
  }, []);

  return { loadModel, getModel };
}
